import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Pagination from "@/components/Pagination";
import { FilterBar, buildQuery } from "@/components/FilterBar";
import PageHeader, { EmptyState, fmtDate } from "@/components/PageHeader";

const STATUSES = [["pending", "Pending"], ["approved", "Approved"], ["returned", "Returned"], ["declined", "Declined"]];
const fmtPts = (p) => (typeof p === "number" ? p.toString() : "—");

// System Admin program-wide view of every Thespian point claim (mirrors Merit System / Letter Points tabs).
export default function AdminThespian() {
  const [filter, setFilter] = useState({ district_id: "", campus_id: "", team_id: "", year: "" });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ status: "", points: "" });

  const load = useCallback(async () => {
    const q = buildQuery(filter);
    try {
      const { data } = await api.get(`/admin/thespian?${q}${q ? "&" : ""}page=${page}&page_size=25`);
      setItems(data.items); setTotal(data.total);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [filter, page]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (c) => { setEditing(c); setForm({ status: c.status, points: String(c.points_awarded ?? c.points_claimed ?? "") }); };
  const save = async () => {
    const payload = { status: form.status };
    if (form.status === "approved") payload.points_awarded = Number(form.points);
    try { await api.put(`/admin/thespian/${editing.id}`, payload); toast.success("Updated"); setEditing(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Thespian Points Management" subtitle="Global view of all Thespian point claims" />
      <FilterBar value={filter} onChange={(v) => { setFilter(v); setPage(1); }} />
      {items.length === 0 ? <EmptyState title="No claims" testId="admin-thespian-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="admin-thespian-table">
            <TableHeader><TableRow className="bg-slate-50">{["Date", "Member", "Campus", "Team", "Activity", "Pts", "Status", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`admin-thespian-row-${c.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDate(c.activity_date)}</TableCell>
                  <TableCell className="text-sm">{c.member_name}</TableCell>
                  <TableCell className="text-sm">{c.campus_name}</TableCell>
                  <TableCell className="text-sm">{c.team_code}</TableCell>
                  <TableCell className="max-w-xs text-sm">{c.activity_name}</TableCell>
                  <TableCell className="font-bold tabular-nums text-amber-700" data-testid={`admin-thespian-pts-${c.id}`}>{fmtPts(c.status === "approved" ? c.points_awarded : c.points_claimed)}</TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`admin-edit-thespian-${c.id}`}>Edit</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Pagination page={page} pageSize={25} total={total} onPage={setPage} />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="admin-thespian-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit claim · {editing?.member_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">{editing?.activity_name} · claimed {fmtPts(editing?.points_claimed)} pts</p>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="admin-thespian-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.status === "approved" && (
              <div className="space-y-2"><Label>Points awarded</Label>
                <Input type="number" min="0.5" max="100" step="0.5" value={form.points} onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))} data-testid="admin-thespian-points" />
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="admin-thespian-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
